import { useState, useEffect, useCallback } from "react";

const STORAGE_KEY = "drumlog_cookie_consent";
const CONSENT_VERSION = "1.0";
const CONSENT_EVENT = "cookieConsentUpdated";

export interface CookieConsents {
  functional: boolean;
  analytics: boolean;
  marketing: boolean;
}

export interface ConsentData {
  version: string;
  timestamp: string;
  consents: CookieConsents;
}

export interface CookieConsentState {
  necessary: true;
  functional: boolean;
  analytics: boolean;
  marketing: boolean;
}

function loadConsent(): ConsentData | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const data: ConsentData = JSON.parse(raw);
    if (data.version !== CONSENT_VERSION) return null;
    return data;
  } catch {
    return null;
  }
}

function saveConsent(consents: CookieConsents): ConsentData {
  const data: ConsentData = {
    version: CONSENT_VERSION,
    timestamp: new Date().toISOString(),
    consents,
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: data }));
  return data;
}

export function useCookieConsent() {
  const [consentData, setConsentData] = useState<ConsentData | null>(() => loadConsent());

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<ConsentData>).detail;
      setConsentData(detail);
    };
    window.addEventListener(CONSENT_EVENT, handler);
    return () => window.removeEventListener(CONSENT_EVENT, handler);
  }, []);

  const hasConsented = consentData !== null;

  const consentState: CookieConsentState = {
    necessary: true,
    functional: consentData?.consents.functional ?? false,
    analytics: consentData?.consents.analytics ?? false,
    marketing: consentData?.consents.marketing ?? false,
  };

  const acceptAll = useCallback(() => {
    const data = saveConsent({ functional: true, analytics: true, marketing: true });
    setConsentData(data);
  }, []);

  const rejectAll = useCallback(() => {
    const data = saveConsent({ functional: false, analytics: false, marketing: false });
    setConsentData(data);
  }, []);

  const acceptSelected = useCallback((consents: CookieConsents) => {
    const data = saveConsent(consents);
    setConsentData(data);
  }, []);

  const resetConsent = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setConsentData(null);
    window.dispatchEvent(new CustomEvent(CONSENT_EVENT));
  }, []);

  return {
    hasConsented,
    consentState,
    consentData,
    acceptAll,
    rejectAll,
    acceptSelected,
    resetConsent,
  };
}
